import { supabase } from "@/integrations/supabase/client";
import { activityService } from "@/services/analytics/ActivityService";
import { ApiService } from "@/services/api/ApiService";
import { EmailService } from "@/services/communication/EmailService";
import { Conversation, InboxQueue } from "@/types/conversation/conversation";
import { LeadInfoField } from "@/types/leads/lead-info";
import type { ResolveMediaResult } from "@/types/voice/genie-attachment";

// A genie's insight set (its configured capture fields). Genies are linked to
// insight sets through a junction table, so the API returns the set as a
// one-item list on some endpoints and as a single object on others.
const insightPreset = (agent: any) => {
  const single = (preset: unknown) => (Array.isArray(preset) ? preset[0] : preset) || null;
  return single(agent?.insights) ?? single(agent?.goal);
};

export class ConversationService {
  public static async getSignedUrl(
    agentId: string,
  ): Promise<{ signed_url: string; conversation_context?: string } | null> {
    try {
      console.log("Getting signed URL for agent: " + agentId);

      const data = await ApiService.callEdge<{ signed_url: string; conversation_context?: string }>(
        "elevenlabs",
        {
          resource: "conversations",
          action: "get-signed-url",
          params: { entityId: agentId },
        },
        "public",
      );

      if (!data) {
        throw new Error("No data returned from Supabase function");
      }

      return data;
    } catch (err) {
      console.error("Error getting signed URL:", err);
      return null;
    }
  }

  static async getAll(
    options: {
      agentIds?: string[];
      timeRange?: string;
      cursor?: string;
      limit?: number;
      excludeMailbox?: boolean;
      conversationType?: string;
      channel?: string;
      ownerId?: string;
      queue?: InboxQueue;
      viewId?: string;
      /** Also count every match (and the unreviewed ones) on the first page. */
      withCounts?: boolean;
    } = {},
  ): Promise<{
    data: Conversation[];
    nextCursor: string | null;
    hasMore: boolean;
    total?: number;
    unreviewed?: number;
  }> {
    const {
      agentIds = [],
      timeRange = "",
      cursor,
      limit = 30,
      excludeMailbox = false,
      conversationType,
      channel,
      ownerId,
      queue,
      viewId,
      withCounts,
    } = options;

    const response = await ApiService.invoke<{
      conversations: any[];
      nextCursor: string | null;
      hasMore: boolean;
      total?: number;
      unreviewed?: number;
    }>({
      resource: "conversations",
      action: "list",
      data: { agentIds, timeRange, cursor, limit, excludeMailbox, conversationType, channel, ownerId, queue, viewId, withCounts },
    });

    return {
      data: (response?.conversations || []).map((c) => this.map(c)),
      nextCursor: response?.nextCursor ?? null,
      hasMore: response?.hasMore ?? false,
      total: response?.total,
      unreviewed: response?.unreviewed,
    };
  }

  static async getById(id: string): Promise<Conversation> {
    const response = await ApiService.invoke<any>({
      resource: "conversations",
      action: "get",
      id,
      data: { skipAnalysis: true },
    });
    return this.map(response);
  }

  static async show(conversation: Conversation): Promise<{
    conversation: Conversation;
    wasUpdated: boolean;
  }> {
    const response = await ApiService.invoke<any>({
      resource: "conversations",
      action: "get",
      id: conversation.id,
    });

    return {
      conversation: this.map(response),
      wasUpdated: true,
    };
  }

  static async delete(conversation: Conversation): Promise<void> {
    await ApiService.invoke({
      resource: "conversations",
      action: "delete",
      id: conversation.id,
    });
  }

  static async clearAgentData(agentId: string): Promise<{ success: boolean; deletedConversations: number }> {
    const response = await ApiService.invoke<{ success: boolean; deletedConversations: number }>({
      resource: "conversations",
      action: "clear-agent-data",
      data: { agentId },
    });
    return response;
  }

  static async exportTranscripts(
    userId: string,
    options: { dateFrom?: string; dateTo?: string } = {},
  ): Promise<{ agentId: string; name: string; content: string }[]> {
    const response = await ApiService.invoke<{ genies: { agentId: string; name: string; content: string }[] }>({
      resource: "conversations",
      action: "export-transcripts",
      data: { userId, dateFrom: options.dateFrom, dateTo: options.dateTo },
    });
    return response?.genies || [];
  }

  static async markAllViewed(): Promise<{ updatedCount: number }> {
    const response = await ApiService.invoke<{ updatedCount: number }>({
      resource: "conversations",
      action: "mark-all-viewed",
    });
    return { updatedCount: response?.updatedCount || 0 };
  }

  static async markAsViewed(conversation: Conversation): Promise<void> {
    await ApiService.invoke({
      resource: "conversations",
      action: "update",
      id: conversation.id,
      data: { viewed: true },
    });
  }

  /** Marks a Listener genie's call or forwarded email handled (or reopens it), which stops its reminder and drops it from the daily brief. */
  static async setHandled(conversationId: number | string, handled: boolean): Promise<void> {
    await ApiService.invoke({
      resource: "conversations",
      action: "update",
      id: conversationId,
      data: { handled_at: handled ? new Date().toISOString() : null },
    });
  }

  /** Gives a call or forwarded email to a teammate (null unassigns); their reminders then go to them only. */
  static async assign(conversationId: number | string, userId: string | null): Promise<void> {
    await ApiService.invoke({
      resource: "conversations",
      action: "update",
      id: conversationId,
      data: { assigned_to: userId },
    });
  }

  static async sync(): Promise<string> {
    const response = await ApiService.invoke<{ message: string }>({
      resource: "conversations",
      action: "sync",
    });
    return response?.message || "Sync completed successfully";
  }

  /** Picks the link/picture to show for a topic the Genie wants to illustrate (showRelatedMedia client tool). */
  static async resolveMedia(params: {
    agentId: string;
    topic: string;
    context?: string | null;
    transcript: Array<{ role: "user" | "agent"; message: string }>;
    /**
     * "own" asks only for the genie's Resources and knowledge base, which answers quickly.
     * "documents" asks for what is inside the genie's documents. Without a stage the lookup
     * also searches the Resources' sites and the web, which takes longer.
     */
    stage?: "own" | "documents";
  }): Promise<ResolveMediaResult> {
    const { data, error } = await supabase.functions.invoke("handle-conversation", {
      body: { action: "resolve-media", data: params },
    });
    if (error) throw new Error(error.message || "Failed to resolve media");
    return { status: data?.status === "found" ? "found" : "not_found", items: data?.items ?? [] };
  }

  static async analyze(conversation: Conversation, fields?: LeadInfoField[], log: boolean = true) {
    try {
      const { data: analysisResult, error: analysisError } = await supabase.functions.invoke("handle-conversation", {
        body: {
          action: "analyse",
          data: {
            conversation: conversation,
            leadInfoFields: fields,
          },
        },
      });

      if (analysisError) {
        throw new Error(`Analysis failed: ${analysisError.message}`);
      }

      const freshData = analysisResult?.conversationDetails || {};

      if (log) {
        await activityService.logActivity({
          activityType: "conversation",
          action: "conversation_analyzed",
          title: `Analyzed conversation with "${conversation.agent?.name || "Unknown Agent"}"`,
          description: `Reviewed ${Math.round((freshData.duration_seconds || 0) / 60)}min conversation`,
          resourceType: "conversation",
          resourceId: conversation.id,
          resourceName: `${conversation.agent?.name || "Unknown Agent"} conversation`,
          category: "conversation",
          durationMs: (freshData.duration_seconds || 0) * 1000,
          metadata: {
            conversation_id: conversation.id,
            elevenlabs_id: conversation.elevenlabs_id,
            agent_id: conversation.agent_id,
            agent_name: conversation.agent?.name,
            status: freshData.status,
            duration: freshData.duration_seconds,
            call_successful: conversation.metadata?.call_successful,
            transcript_summary: conversation.metadata?.transcript_summary,
          },
        });
      }

      if (conversation.agent.send_conv_reports) {
        EmailService.sendAnalysisEmail(conversation);
      }

      return freshData;
    } catch (error) {
      console.error(`Failed to analyze conversation ${conversation.id}:`, error);
      throw error;
    }
  }

  static map(data: any): Conversation {
    return {
      id: data.id,
      agent_id: data.agent_id,
      elevenlabs_id: data.elevenlabs_id,
      el_agent_id: data.el_agent_id || data.agent?.elevenlabs_id,
      type: data.type || null,
      viewed: data.viewed || false,
      viewed_at: data.viewed_at || null,
      handled_at: data.handled_at ?? null,
      attention: data.attention ?? null,
      assigned_to: data.assigned_to ?? null,
      assigned_at: data.assigned_at ?? null,
      metadata: data.metadata || null,
      isSynced: data.isSynced || (data.conversation_details && data.conversation_details.length > 0),
      details: {
        id: data.conversation_details?.[0]?.id || data.details?.id || "",
        conv_id:
          data.conversation_details?.[0]?.conversation_id ||
          data.details?.conversation_id ||
          data.details?.conv_id ||
          null,
        agent_id: data.agent_id,
        status: data.conversation_details?.[0]?.status || data.details?.status || null,
        duration:
          data.conversation_details?.[0]?.duration_seconds ||
          data.details?.duration_seconds ||
          data.details?.duration ||
          null,
        summary: data.conversation_details?.[0]?.summary || data.details?.summary || null,
        transcript: data.conversation_details?.[0]?.transcript || data.details?.transcript || null,
        goals: data.conversation_details?.[0]?.goals || data.details?.goals || null,
        topics: data.conversation_details?.[0]?.topics || data.details?.topics || null,
        lead_info: data.conversation_details?.[0]?.lead_info || data.details?.lead_info || null,
        analysis_result:
          data.conversation_details?.[0]?.analysis_result ||
          data.details?.analysis_result ||
          data.details?.goal_analysis ||
          null,
        conversation_date:
          data.conversation_details?.[0]?.conversation_date ||
          data.details?.conversation_date ||
          data.details?.ended_at ||
          null,
        last_synced_at: data.conversation_details?.[0]?.last_synced_at || data.details?.last_synced_at || null,
        created_at: data.conversation_details?.[0]?.created_at || data.details?.created_at || null,
        updated_at: data.conversation_details?.[0]?.updated_at || data.details?.updated_at || null,
        escalation: data.conversation_details?.[0]?.escalation || data.details?.escalation || null,
      },
      agent: {
        id: data.agent?.id || "",
        elevenlabs_id: data.agent?.elevenlabs_id || "",
        user_id: data.agent?.user_id || "",
        name: data.agent?.name || "",
        description: data.agent?.description || "",
        brand: data.agent?.brand || "",
        is_active: data.agent?.is_active || false,
        settings: data.agent?.settings || null,
        category: data.agent?.category || "",
        created_at: data.agent?.created_at || "",
        updated_at: data.agent?.updated_at || "",
        send_conv_reports: data.agent?.send_conv_reports || false,
        branding: data.agent?.branding || null,
        agent_phone_numbers: data.agent?.agent_phone_numbers || [data.agent?.phone] || [],
        goal: insightPreset(data.agent),
        insights: insightPreset(data.agent),
        redirect_url: data.agent?.redirect_url || null,
        voice: data.agent?.voice || data.agent?.settings?.conversation_config?.tts?.voice_id || null,
      },
      medias: data.medias || [],
    };
  }
}
